import React, { useState, useEffect } from "react";
import { ShowButton } from "@refinedev/antd";
import { useList, useCustomMutation, useNotification } from "@refinedev/core";
import { Table, Space, Tag, Button, Card, Input } from "antd";
import { SearchOutlined, PlusOutlined, FileTextOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { PageHeader } from "../../components/ui";

interface PurchaseOrder {
    id: number;
    orderNumber: string;
    vendorId: number;
    vendorName?: string;
    orderDate: string;
    deliveryDate: string;
    status: string;
    totalAmount: number;
    documentUrl?: string;
    createdAt: string;
}

export const PurchaseOrderList: React.FC = () => {
    const [searchText, setSearchText] = useState<string>("");
    const [filteredItems, setFilteredItems] = useState<PurchaseOrder[]>([]);
    const navigate = useNavigate();
    const { open } = useNotification();

    const { data, isLoading, refetch } = useList({
        resource: "purchaseorders",
    });

    const { mutate: confirm } = useCustomMutation();
    const { mutate: cancel } = useCustomMutation();
    const { mutate: convert } = useCustomMutation();
    const { mutate: generatePdf } = useCustomMutation();

    useEffect(() => {
        if (!data?.data) return;

        let orders = data.data as PurchaseOrder[];

        if (searchText.trim() !== "") {
            const searchLower = searchText.toLowerCase();
            orders = orders.filter((order: PurchaseOrder) =>
                order.orderNumber?.toLowerCase().includes(searchLower) ||
                order.status?.toLowerCase().includes(searchLower)
            );
        }

        const sorted = [...orders].sort((a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        setFilteredItems(sorted);
    }, [data, searchText]);

    const handleConfirm = (id: number) => {
        confirm({ url: `purchaseorders/${id}/confirm`, method: "post" }, { onSuccess: () => refetch() });
    };

    const handleCancel = (id: number) => {
        cancel({ url: `purchaseorders/${id}/cancel`, method: "post" }, { onSuccess: () => refetch() });
    };

    const handleConvert = (id: number) => {
        convert({ url: `purchaseorders/${id}/convert`, method: "post" }, { onSuccess: () => refetch() });
    };

    const handlePrint = (record: PurchaseOrder) => {
        if (record?.documentUrl) {
            window.open(record.documentUrl, "_blank");
            return;
        }
        generatePdf(
            {
                url: `purchaseorders/${record.id}/generate-pdf`,
                method: "post",
                successNotification: () => ({
                    message: "Success",
                    description: "Purchase order PDF generated successfully",
                    type: "success",
                }),
            },
            {
                onSuccess: (data: any) => {
                    const documentUrl = data?.data?.DocumentUrl || data?.DocumentUrl;
                    if (documentUrl) {
                        window.open(documentUrl, "_blank");
                    }
                    refetch();
                },
            }
        );
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case "Confirmed": return "success";
            case "Draft": return "default";
            case "Converted": return "purple";
            case "Cancelled": return "error";
            default: return "default";
        }
    };

    const columns = [
        {
            title: "PO #",
            dataIndex: "orderNumber",
            key: "orderNumber",
            render: (value: string) => (
                <span className="font-medium text-text-primary">{value || "-"}</span>
            ),
        },
        {
            title: "Order Date",
            dataIndex: "orderDate",
            key: "orderDate",
            render: (value: string) => (
                <span className="text-text-secondary text-sm">
                    {value ? new Date(value).toLocaleDateString() : "-"}
                </span>
            ),
        },
        {
            title: "Delivery Date",
            dataIndex: "deliveryDate",
            key: "deliveryDate",
            render: (value: string) => (
                <span className="text-text-secondary text-sm">
                    {value ? new Date(value).toLocaleDateString() : "-"}
                </span>
            ),
            responsive: ['md'] as any,
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (value: string) => (
                <Tag color={getStatusColor(value)} className="status-tag">{value}</Tag>
            ),
        },
        {
            title: "Amount",
            dataIndex: "totalAmount",
            key: "totalAmount",
            render: (value: number) => (
                <span className="font-semibold text-primary">${value?.toFixed(2) || '0.00'}</span>
            ),
        },
        {
            title: "Created",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (value: string) => (
                <span className="text-text-secondary text-sm">
                    {value ? new Date(value).toLocaleDateString() : "-"}
                </span>
            ),
            responsive: ['lg'] as any,
        },
        {
            title: "Actions",
            key: "actions",
            width: 220,
            render: (_: any, record: PurchaseOrder) => (
                <Space size="small" wrap>
                    <ShowButton hideText size="small" recordItemId={record.id} resource="purchaseorders" />
                    {record.status === "Draft" && (
                        <Button size="small" type="primary" onClick={() => handleConfirm(record.id)}>Confirm</Button>
                    )}
                    {(record.status === "Draft" || record.status === "Confirmed") && (
                        <Button size="small" danger onClick={() => handleCancel(record.id)}>Cancel</Button>
                    )}
                    {record.status === "Confirmed" && (
                        <Button size="small" type="primary" onClick={() => handleConvert(record.id)}>Convert</Button>
                    )}
                    {(record.status === "Draft" || record.status === "Confirmed") && (
                        <Button size="small" onClick={() => handlePrint(record)}>Print</Button>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div className="page-container">
            <PageHeader
                title="Purchase Orders"
                icon={<FileTextOutlined />}
                breadcrumbs={[{ title: "Purchase Orders" }]}
                actions={[
                    {
                        label: "Create",
                        onClick: () => navigate("/purchaseorders/create"),
                        type: "primary",
                        icon: <PlusOutlined />,
                    },
                ]}
            />

            <div className="data-table-card">
                <Card>
                    <div style={{ marginBottom: 16 }}>
                        <Input
                            placeholder="Search by order number or status"
                            prefix={<SearchOutlined className="text-text-muted" />}
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                            style={{ maxWidth: 400 }}
                            allowClear
                            size="large"
                        />
                    </div>

                    <Table
                        dataSource={filteredItems}
                        columns={columns}
                        rowKey="id"
                        loading={isLoading}
                        pagination={{
                            pageSize: 10,
                            showSizeChanger: true,
                            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
                        }}
                        className="custom-table"
                        scroll={{ x: 900 }}
                    />
                </Card>
            </div>
        </div>
    );
};
